import { useEffect, useMemo, useRef } from "react";
import type { CoachingNote, Player } from "@/lib/types";
import { formatPlayhead } from "@/features/playback/useVideoPlayhead";
import {
  NOTE_CATEGORY_LABELS,
  coerceNoteCategory,
} from "@/features/coach-review/components/Composer";
import { useThumbnail } from "@/features/coach-review/hooks/useThumbnail";

/**
 * Timeline chip rail — the horizontal strip of the selected
 * match's coaching notes under the Coach Review video. Ports
 * legacy `js/coaching/review.js:renderCoachReviewNotes`.
 *
 * Each chip shows `thumbnail · MM:SS · player/team indicator ·
 * category dot · short title`, sorted left-to-right by
 * `timestamp_seconds` so the rail reads in match order. Clicking a
 * chip seeks the video to that note's moment (via `onSeek`) — the
 * M5 P0-2 playhead infra is what makes the seek land somewhere
 * meaningful.
 *
 * The per-note thumbnail comes from `useThumbnail("note", id)` —
 * an auth-Bearer `fetch → blob → objectURL` (the endpoint is
 * access-controlled per viewer, so `<img src>` can't load it).
 * Until the blob resolves (and on any failure — no thumbnail yet,
 * viewer can't see it, network error) the chip shows a film-strip
 * placeholder, never a broken-image icon.
 *
 * Scope: video notes only. The rail filters to
 * `note_context === "video"` with a `match_id` matching the
 * selected match — observations have no moment to jump to.
 *
 * The active chip (the last one seeked, tracked by the parent)
 * gets `is-active` styling + an `aria-pressed` flag, and is
 * nudged into view with `scrollIntoView` so a long rail keeps the
 * focused note visible after a seek.
 */

export type TimelineChipRailProps = {
  /** Selected match id — `undefined` renders the no-match state. */
  matchId: string | undefined;
  /** The match's notes (from `useCoachNotes`). */
  notes: CoachingNote[];
  /** Active-team roster — drives the player/jersey indicator. */
  players: Player[];
  /** The currently-focused note id (last seeked), or `null`. */
  activeNoteId: number | null;
  /** Click a chip → seek the video + restore the note's drawing. */
  onSeek: (note: CoachingNote) => void;
  /** True while the notes query is still loading. */
  isLoading?: boolean;
};

/** Build the player/team indicator for one note's `player_ids`.
 * Mirrors the legacy branch ladder: 1 player → jersey-or-first-name,
 * 2+ → `+N`, none → `Team`. */
function playerIndicator(
  ids: string[],
  playersById: Map<string, Player>,
): { label: string; aria: string } {
  if (ids.length === 1) {
    const p = playersById.get(ids[0]);
    if (p?.jersey_number) {
      return { label: `#${p.jersey_number}`, aria: `, player ${p.jersey_number}` };
    }
    if (p?.display_name) {
      const first = p.display_name.split(" ")[0];
      return { label: first, aria: `, player ${p.display_name}` };
    }
    return { label: "Player", aria: ", one player" };
  }
  if (ids.length > 1) {
    return { label: `+${ids.length}`, aria: `, ${ids.length} players` };
  }
  return { label: "Team", aria: ", team-wide" };
}

export function TimelineChipRail({
  matchId,
  notes,
  players,
  activeNoteId,
  onSeek,
  isLoading,
}: TimelineChipRailProps) {
  // Video notes for THIS match, sorted by timestamp so the rail
  // reads left-to-right in match order. Observations carry no
  // moment — they're filtered out.
  const railNotes = useMemo(() => {
    if (!matchId) return [];
    return notes
      .filter((n) => n.note_context === "video" && n.match_id === matchId)
      .slice()
      .sort(
        (a, b) =>
          Number(a.timestamp_seconds ?? 0) - Number(b.timestamp_seconds ?? 0),
      );
  }, [notes, matchId]);

  const playersById = useMemo(() => {
    const m = new Map<string, Player>();
    for (const p of players) m.set(String(p.id), p);
    return m;
  }, [players]);

  // Nudge the active chip into view after a seek — `block: nearest`
  // so it doesn't scroll the page, `inline: center` so the focused
  // note ends up mid-rail on a long strip.
  const railRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (activeNoteId == null) return;
    const chip = railRef.current?.querySelector<HTMLButtonElement>(
      `[data-coach-note-id="${activeNoteId}"]`,
    );
    chip?.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
  }, [activeNoteId]);

  if (!matchId) {
    return (
      <div
        className="text-ink-muted text-xs font-mono px-3 py-2"
        data-testid="coach-review-timeline-empty"
        aria-label="Notes timeline (no match selected)"
      >
        Select a match to see its notes.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        className="text-ink-muted text-xs font-mono px-3 py-2"
        data-testid="coach-review-timeline-loading"
      >
        Loading notes…
      </div>
    );
  }

  if (railNotes.length === 0) {
    return (
      <div
        className="text-ink-muted text-xs font-mono px-3 py-2"
        data-testid="coach-review-timeline-empty"
      >
        No notes for this match yet — save your first one above.
      </div>
    );
  }

  return (
    <div
      ref={railRef}
      className="flex gap-2 overflow-x-auto pb-1"
      data-testid="coach-review-timeline-rail"
      aria-label="Notes timeline"
      role="group"
    >
      {railNotes.map((n) => {
        const ids = (n.player_ids ?? []).map(String);
        const { label: pLabel, aria: pAria } = playerIndicator(ids, playersById);
        const cat = coerceNoteCategory(n.category);
        const catLabel = NOTE_CATEGORY_LABELS[cat];
        const ts = formatPlayhead(n.timestamp_seconds);
        const active = activeNoteId === n.id;
        const ariaLabel = `Jump to ${ts}${pAria}, ${catLabel}: ${n.title}`;
        return (
          <button
            key={n.id}
            type="button"
            data-coach-note-id={n.id}
            data-coach-category={cat}
            data-testid="coach-review-timeline-chip"
            className={[
              "shrink-0 flex flex-col gap-1 rounded-md border px-2.5 py-2 text-left transition-colors",
              "min-w-[8.5rem] max-w-[12rem]",
              active
                ? "border-accent bg-accent/10"
                : "border-rule bg-surface/50 hover:border-border-strong hover:bg-surface",
            ].join(" ")}
            title={ariaLabel}
            aria-label={ariaLabel}
            aria-pressed={active}
            onClick={() => onSeek(n)}
          >
            <ChipThumbnail noteId={n.id} />

            <span className="flex items-center gap-1.5 text-[0.7rem]">
              <span className="font-mono text-ink tabular-nums">{ts}</span>
              <span className="text-ink-muted">{pLabel}</span>
              <span
                className="inline-block h-1.5 w-1.5 rounded-full bg-accent shrink-0"
                aria-hidden="true"
                title={catLabel}
              />
            </span>
            <span className="text-xs text-ink truncate">{n.title}</span>
          </button>
        );
      })}
    </div>
  );
}

/** One chip's thumbnail tile. `useThumbnail` resolves the
 * access-controlled JPEG via an auth-Bearer blob fetch; until it
 * lands (and on any failure) the film-strip placeholder shows —
 * never a broken-image icon, and the placeholder path makes no
 * network request of its own. Scoped to its own component so each
 * chip's hook keys cleanly on its note id. */
function ChipThumbnail({ noteId }: { noteId: number }) {
  const url = useThumbnail("note", noteId);
  if (url) {
    return (
      <img
        src={url}
        alt=""
        aria-hidden="true"
        data-testid="coach-review-timeline-chip-thumb"
        className="block h-12 w-full rounded object-cover border border-rule/60"
      />
    );
  }
  return (
    <span
      className="block h-12 w-full rounded bg-gradient-to-br from-canvas to-surface border border-rule/60"
      aria-hidden="true"
      data-testid="coach-review-timeline-chip-thumb-placeholder"
    />
  );
}
